/**
 * Firebase Performance custom traces for business metrics
 * 
 * These traces are designed to fire once per session/query and fail silently
 * if Firebase Performance is unavailable.
 */

import { trace } from "firebase/performance";
import { performance } from "../services/firebaseConfig";

/**
 * Safely get a trace instance, returning null if Performance is unavailable
 */
function getTraceSafe(traceName) {
  try {
    return trace(performance, traceName);
  } catch (error) {
    // Fail silently if Performance is unavailable
    return null;
  }
}

// Module-level singleton for app_ready_time trace
let appReadyTrace = null;
let appReadyTraceStarted = false;

/**
 * Start app_ready_time trace (fires once per app session)
 * Call this as early as possible in app startup
 */
export function startAppReadyTime() {
  if (appReadyTraceStarted) return; // Already started
  
  try {
    appReadyTrace = getTraceSafe("app_ready_time");
    if (appReadyTrace) {
      appReadyTrace.start();
      appReadyTraceStarted = true;
    }
  } catch (error) {
    // Fail silently
  }
}

/**
 * Stop app_ready_time trace (fires once per app session)
 * Call this when auth is ready and app is usable
 */
export function stopAppReadyTime() {
  if (!appReadyTraceStarted || !appReadyTrace) return; // Not started or already stopped
  
  try {
    appReadyTrace.stop();
    appReadyTrace = null; // Prevent multiple stops
  } catch (error) {
    // Fail silently
  }
}

// Module-level Map to track tasks_initial_load_time traces by queryKey
const tasksLoadTraces = new Map(); // queryKey -> trace instance

/**
 * Generate a stable queryKey for tasks query
 * @param {string} uid - User ID
 * @param {string} workspace - Workspace name
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {string} - Query key
 */
function getTasksQueryKey(uid, workspace, startDate, endDate) {
  // Include uid in key for per-user tracking, but trace name stays fixed
  // This allows tracking multiple users' queries separately
  return `${uid}_${workspace}_${startDate}_${endDate}`;
}

/**
 * Start tasks_initial_load_time trace (fires once per unique query per session)
 * @param {string} uid - User ID
 * @param {string} workspace - Workspace name
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 */
export function startTasksInitialLoadTime(uid, workspace, startDate, endDate) {
  if (!uid || !workspace || !startDate || !endDate) return;
  
  const queryKey = getTasksQueryKey(uid, workspace, startDate, endDate);
  
  // Only start if we haven't started a trace for this queryKey yet
  if (tasksLoadTraces.has(queryKey)) return;
  
  try {
    const traceInstance = getTraceSafe("tasks_initial_load_time");
    if (traceInstance) {
      traceInstance.start();
      tasksLoadTraces.set(queryKey, traceInstance);
    }
  } catch (error) {
    // Fail silently
  }
}

/**
 * Stop tasks_initial_load_time trace for a specific query (fires once per query)
 * @param {string} uid - User ID
 * @param {string} workspace - Workspace name
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 */
export function stopTasksInitialLoadTime(uid, workspace, startDate, endDate) {
  if (!uid || !workspace || !startDate || !endDate) return;
  
  const queryKey = getTasksQueryKey(uid, workspace, startDate, endDate);
  const traceInstance = tasksLoadTraces.get(queryKey);
  
  if (!traceInstance) return; // Not started or already stopped
  
  try {
    traceInstance.stop();
    tasksLoadTraces.delete(queryKey); // Prevent multiple stops
  } catch (error) {
    // Fail silently
    tasksLoadTraces.delete(queryKey); // Clean up on error
  }
}

export async function withTaskCreateTime(asyncFn) {
  const traceInstance = getTraceSafe("task_create_time");
  let traceStarted = false;
  
  try {
    if (traceInstance) {
      traceInstance.start();
      traceStarted = true;
    }
    return await asyncFn();
  } catch (error) {
    throw error;
  } finally {
    if (traceStarted && traceInstance) {
      try {
        traceInstance.stop();
      } catch (error) {
        // Fail silently
      }
    }
  }
}

export async function withTaskUpdateTime(asyncFn) {
  const traceInstance = getTraceSafe("task_update_time");
  let traceStarted = false;
  
  try {
    if (traceInstance) {
      traceInstance.start();
      traceStarted = true;
    }
    return await asyncFn();
  } catch (error) {
    throw error;
  } finally {
    if (traceStarted && traceInstance) {
      try {
        traceInstance.stop();
      } catch (error) {
        // Fail silently
      }
    }
  }
}
